/**
 * HOW MUCH A DIG MAY SPEND, chosen where the dig is started.
 *
 * It used to sit in the run controls, away from the Research window, so the
 * spend was decided somewhere other than where the dig was. Now the window that
 * starts the dig asks it. The last choice is remembered per browser
 * (`rememberBudget`), so a room dug again and again opens at the same size, and
 * a bigger dig is one change away when pressing Research.
 */
import { budgetCapNote, budgetOptionsFor, clampBudget } from './intents';

export default function BudgetPicker({
  budget,
  onBudget,
  maxBudget = null,
  budgetCappedByCredits = false,
  creditsLine = null,
}: {
  budget: number;
  onBudget: (tokens: number) => void;
  /** The ceiling on one run: the lower of the plan's cap and what the balance
   *  funds. Nothing above it is offered. */
  maxBudget?: number | null;
  /** True when the balance, not the plan, is doing the capping. */
  budgetCappedByCredits?: boolean;
  /** "312 credits · about 2 digs", when the page knows it. */
  creditsLine?: string | null;
}) {
  const value = clampBudget(budget, maxBudget);
  const note = budgetCapNote(maxBudget, budgetCappedByCredits);
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400" data-testid="budget-picker">
      <span>Spend up to</span>
      <select
        aria-label="Token budget"
        value={value}
        onChange={e => onBudget(Number(e.target.value))}
        className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1 py-0.5 text-xs"
      >
        {budgetOptionsFor(maxBudget).map(b => (
          <option key={b.tokens} value={b.tokens}>
            {b.label} tokens
          </option>
        ))}
      </select>
      {note && (
        <span
          className="text-amber-700 dark:text-amber-300"
          data-testid="budget-capped"
          title={
            budgetCappedByCredits
              ? 'A run cannot spend more than your balance funds. The server caps it either way; this is the cap.'
              : 'Bigger budgets are available on a higher plan. The server caps a run at this either way.'
          }
        >
          {note}
        </span>
      )}
      {creditsLine && (
        <span className="tabular-nums" data-testid="budget-credits">
          · {creditsLine}
        </span>
      )}
    </div>
  );
}
